import { Middleware } from '@reduxjs/toolkit';
import packageJson from '../../../package.json';

/**
 * Lightweight "flight recorder" for debugging issues that only reproduce on
 * real devices (typically mobile). A ring buffer keeps the most recent Redux
 * actions in memory; on error we snapshot that buffer together with the current
 * state and device context and ship it to /api/error.
 */

const MAX_ACTIONS = 40;
// Cap per-action payload + per-snapshot sizes so we never post megabytes from a
// flaky mobile connection.
const MAX_PAYLOAD_CHARS = 2000;
const MAX_ENTITY_COLLECTION = 50;

export interface RecordedAction {
  type: string;
  t: number; // ms since the buffer started (relative timestamps keep it small)
  payload?: any;
}

export interface DebugSnapshot {
  version: string;
  url: string;
  userAgent: string;
  capturedAt: string;
  actions: RecordedAction[];
  state: unknown;
}

let buffer: RecordedAction[] = [];
const startedAt = Date.now();

function truncate(value: unknown): unknown {
  if (value == null) return value;
  try {
    const json = JSON.stringify(value);
    if (json.length <= MAX_PAYLOAD_CHARS) return value;
    return `<<truncated ${json.length} chars>>`;
  } catch {
    return '<<unserializable>>';
  }
}

export const actionHistoryMiddleware: Middleware = () => next => (action: any) => {
  if (action && typeof action.type === 'string') {
    buffer.push({ type: action.type, t: Date.now() - startedAt, payload: truncate(action.payload) });
    if (buffer.length > MAX_ACTIONS) buffer.shift();
  }
  return next(action);
};

export function getActionHistory(): RecordedAction[] {
  return buffer.slice();
}

export function clearActionHistory(): void {
  buffer = [];
}

/** Shrink large entity collections so the snapshot stays small. */
function sanitizeState(state: any): unknown {
  const ent = state?.entities;
  if (!ent) return state;
  const sanitized: Record<string, any> = {};
  for (const key of Object.keys(ent)) {
    const col = ent[key];
    const ids = col ? Object.keys(col) : [];
    sanitized[key] = ids.length > MAX_ENTITY_COLLECTION ? `<<${ids.length} items — truncated>>` : col;
  }
  return { ...state, entities: sanitized };
}

/**
 * Build a serialisable snapshot of recent actions + current state + device
 * context. Pass the store's getState (kept loose to avoid an import cycle).
 */
export function buildDebugSnapshot(getState: () => unknown): DebugSnapshot {
  return {
    version: packageJson.version,
    url: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    capturedAt: new Date().toISOString(),
    actions: getActionHistory(),
    state: sanitizeState(getState()),
  };
}
